/* THE SUPABASE BACKEND — what sits behind the seam
 *
 * PHASE 3, STEP 4. Like `src/idb.js`, `src/auth.js` and `src/mapper.js`,
 * this file is NEW CODE rather than moved code: `scripts/extract.mjs`
 * does not generate it. Everything else in src/ except those three is
 * regenerated from the HTML source, so a hand edit there is silently
 * lost — see BUILD_NOTES.md.
 *
 * ── WHAT THIS IS ───────────────────────────────────────────────────
 * The four-method backend the app has been calling since Phase 1. It
 * takes the same `get`/`set`/`delete`/`list` and routes each key to
 * whichever store now owns it. No call site changes; `src/storage.js`
 * keeps its body byte-identical to the original.
 *
 *   notevault:collection        →  Postgres `holdings`
 *   notevault:customnotes       →  THE SAME ROWS, `custom_entry` column
 *   notevault:profile           →  Postgres `profiles`, the caller's row
 *   notevault:photo:*           →  IndexedDB, through idb.js
 *   notevault:pubph:*           →  IndexedDB
 *   notevault:pub:*             →  IndexedDB   (Step 6 makes this is_public)
 *   notevault:brand*:*          →  IndexedDB
 *
 * `src/idb.js` is NOT deleted — it is demoted to the photo backend.
 * Photos cannot move until Phase 4 (the tier encoder) and Phase 5 (the
 * userId that names an R2 object key) exist, so the bytes stay local and
 * `photo_front_ref` carries `idb:<holdingId>:front` to say so.
 *
 * ── ⚠ THE KEYS ARE STILL `notevault:` AND THAT IS NOW DELIBERATE ───
 * The brief asks for `notevault:*` → `currencykeep:*` in Phase 3, and
 * the carry-forward called this the natural moment for it "since every
 * one of those keys is being rewritten anyway".
 *
 * They are no longer being rewritten. Jesse cut the legacy-id migration
 * (2026-08-08, fail closed), and renaming the photo namespace WITHOUT a
 * migration does the one thing the migration existed to prevent: it
 * orphans every photo already in the store, silently. The rename lost
 * its justification the moment its carrier was cut, so it travels with
 * the migration whenever that is built.
 *
 * ── ⚠ TWO SEAM KEYS, ONE TABLE ─────────────────────────────────────
 * This is the part that is not visible from either side alone.
 *
 * `notevault:collection` and `notevault:customnotes` are two separate
 * keys to the app and ONE table here: a quick-add note's catalog entry
 * rides on its holding, in `custom_entry`, because Step 3 found that
 * writing them apart makes the note VANISH on a second device rather
 * than fail (views-vault.js:26 and views-community.js:34 are each
 * `.filter(o => o.n)`).
 *
 * So neither `set` carries enough on its own to build a payload, and
 * `db/004`'s CHECK makes a `CUSTOM:` holding without its entry a hard
 * 23514. This module therefore keeps BOTH halves and merges on every
 * write.
 *
 * ⚠ AND THE TEMPTING SHORTCUT IS WRONG. It looks like `customnotes`
 * could be a cache-only write, syncing only when the collection is set —
 * one RPC per save instead of two. It cannot: main.js:53,56,57 call
 * `saveCustom()` ALONE when the collector edits a quick-add note's Fr.
 * number, denomination or section. That shortcut silently drops exactly
 * those three edits. Checked by grep, not by reading.
 *
 * The cost of doing it correctly is stated rather than discovered: a
 * quick-add issues two full-vault syncs (`saveCustom(); save();`), and
 * the first is a wasted write whose new entry has no holding to ride on
 * yet. Correct, idempotent, and worth coalescing only with measurements.
 *
 * ── ⚠ ONE READ FILLS BOTH HALVES, AND THE ORDER IS WHY ─────────────
 * main.js:67 is `await load(); await loadProfile(); await loadCustom();`
 * — and `load()` calls `migrateIds()`, which can call `save()`. A write
 * issued before `loadCustom()` has run is a write with no custom half:
 * quick-add holdings would go out with `custom_entry` NULL, which is
 * precisely the vanishing note `db/004` exists to prevent, manufactured
 * by the write path itself — the same defect `db/003` had.
 *
 * So the FIRST read of either key does the one table read and caches
 * both halves. `get("notevault:customnotes")` never issues a second,
 * differently-timed read of the same rows.
 *
 * ── ⚠ THE THREE RULES THAT KEEP A VAULT ALIVE ──────────────────────
 * Carry-forward #2, and the database can only see part of it.
 *
 * 1. NEVER WRITE FROM A READ THAT DID NOT SUCCEED. `state.js:14` is
 *    `try{...}catch(e){ state.collection=[] }` — ANY read failure empties
 *    the vault in memory. Against IndexedDB that is nearly unheard of;
 *    against Supabase it is ordinary. Load fails → the vault is [] → the
 *    collector taps anything → `save()` → a full-replace sync deletes
 *    every note they own.
 *
 *    `sync_holdings` refuses the TOTAL case (`p_allow_empty`). It cannot
 *    see the partial one: a vault of 40 that comes back as 12 looks
 *    exactly like a vault of 12, and the sync deletes 28 notes. Worse,
 *    the guard does not fire at all once the collector adds one note to
 *    an empty-because-failed vault — a payload of 1 is not empty, and
 *    delete-not-in-list removes the other 39.
 *
 *    So `_read` is set ONLY by a fully verified read, and every write is
 *    refused while it is null. That is the guard the database cannot be.
 *
 * 2. A SHORT READ IS A FAILED READ, NOT A SMALL VAULT. PostgREST always
 *    reports the true total in `content-range`; `roundtrip-check` proves
 *    the detector works. Compared on every read, and a read whose total
 *    cannot be established at all is treated as failed rather than
 *    trusted — an unverifiable read must not be indistinguishable from a
 *    verified one.
 *
 * 3. `p_allow_empty` IS PASSED ONLY AFTER A SUCCESSFUL READ. Which rule
 *    1 already guarantees: the only way to reach a zero-row payload here
 *    is a verified read followed by the collector deleting their last
 *    note, which has to keep working or the guard is a bug.
 *
 * ── ⚠ THE PROFILE HAS THE SAME SHAPE OF BUG, AND WORSE TEETH ───────
 * Not in the carry-forwards; found reading community.js against the
 * schema. `loadProfile()` (community.js:16) is another bare `catch(e){}`,
 * so a failed read leaves `state.username = ""` and `isPublic = false` —
 * and `saveProfile()` will then happily PATCH that over the real row.
 *
 * Blanking a username is not like losing a note: `db/001`'s
 * `username_lock` trigger allows one change per YEAR. A failed read plus
 * one profile save could cost a collector their name for twelve months.
 * So profile writes are gated on a successful profile read too.
 *
 * ── FAIL CLOSED WHEN SIGNED OUT ────────────────────────────────────
 * The grants give `profiles`/`holdings` to `authenticated` and NOTHING
 * to `anon`, and a browser holding only the publishable key IS `anon`.
 * With no session this module refuses reads and writes rather than
 * falling back to IndexedDB.
 *
 * That fallback was considered and rejected by Jesse, 2026-08-08: it is
 * two sources of truth for one vault, and reconciling them later is the
 * silent data-divergence this phase exists to prevent.
 *
 * The sign-in UI is Step 6. Until it lands, a signed-out app browses the
 * catalog and says plainly that it cannot reach the vault.
 */

import { getSession, getToken, refresh } from './auth.js'
import { backend as local, requestPersistence } from './idb.js'
import {
  indexCustom,
  profileToRow,
  rowToCustomEntry,
  rowToProfile,
  rowsToVault,
  textIn,
  vaultToRows,
} from './mapper.js'

/* ── the three keys this module owns ───────────────────────────────── */

const COLLECTION_KEY = 'notevault:collection'
const CUSTOM_KEY = 'notevault:customnotes'
const PROFILE_KEY = 'notevault:profile'
const REMOTE_KEYS = [COLLECTION_KEY, CUSTOM_KEY, PROFILE_KEY]

/* Resolved through globalThis first so this module also runs under the
 * gate, where there is no browser. Same indirection auth.js and idb.js
 * use. */
const env = (name) =>
  (typeof import.meta !== 'undefined' && import.meta.env?.[name]) ??
  globalThis.__CK_ENV?.[name] ??
  undefined

const BASE = () => String(env('VITE_SUPABASE_URL') ?? '').replace(/\/$/, '')
const ANON_KEY = () => String(env('VITE_SUPABASE_PUBLISHABLE_KEY') ?? '')

/* ── what the collector is told ────────────────────────────────────── */

/* Short, true, and never the raw body — a database message can carry
 * more detail than belongs on screen. The state.js patch shows these. */
const MSG = {
  signedOut: 'Sign in to load your vault',
  readFailed: 'Could not load your vault — your notes are safe, but this device cannot see them right now',
  saveFailed: 'Could not save — this change is not stored yet',
  offline: 'No connection — this change is not stored yet',
}

/* ── errors that say what kind of failure they are ─────────────────── */

/*
 * ⚠ CARRY-FORWARD #5. `storage.js:19-31` retries ANY thrown error three
 * times with 220ms and 440ms backoff. That is right for a transient
 * IndexedDB failure and wrong for most of what Supabase returns.
 *
 * WHAT THIS MODULE DOES ABOUT IT, and what it deliberately does not:
 *
 *   - The 401 case — the one that matters — is handled HERE, not there:
 *     one refresh and one retry, inside `authed()`, so it never reaches
 *     the blind loop.
 *   - Every error carries `permanent`, `code` and `hint` so a caller,
 *     a gate, or a future storage.js can act on the classification.
 *   - The retry loop itself is NOT patched. It is moved-verbatim code
 *     and the carry-forward's own assessment is that retrying a
 *     permanent failure "costs 660ms and reports the same error, so it
 *     is not dangerous" — `sync_holdings` is a full replace and
 *     therefore idempotent, so three identical refusals are three
 *     identical refusals. Changing verbatim code for 660ms is not a
 *     trade worth making without asking. Left as an open item.
 */
class RemoteError extends Error {
  constructor(message, info = {}) {
    super(message)
    this.name = 'RemoteError'
    this.status = info.status ?? 0
    this.code = info.code ?? null
    this.hint = info.hint ?? null
    this.permanent = Boolean(info.permanent)
    this.userMessage = info.userMessage ?? MSG.saveFailed
  }
}

/* Every deliberate refusal in `sync_holdings` carries a machine-readable
 * hint. The hint field is there to be read. */
const PERMANENT_HINTS = new Set([
  'sync_holdings.no_session',
  'sync_holdings.bad_payload',
  'sync_holdings.empty_refused',
  'sync_holdings.unknown_field',
])

/* From the carry-forward's table, which was drawn from the round-trip
 * gate's own measured results rather than from documentation. */
const PERMANENT_CODES = new Set([
  'P0001',    // raise exception — every deliberate refusal above
  '22P02',    // bad uuid / date in the payload
  '42501',    // RLS refusal, or a missing grant
  '21000',    // duplicate id in one payload (ON CONFLICT hit twice)
  '23514',    // CHECK violation — db/004's custom_entry invariant
  '23505',    // unique violation — a taken username
  '23503',    // FK violation
  'PGRST202', // no such function — the migration has not run
  'PGRST204', // no such column — the migration has not run
  'PGRST205', // no such table/view
])

function isPermanent(status, code, hint) {
  if (hint && PERMANENT_HINTS.has(hint)) return true
  if (code && PERMANENT_CODES.has(code)) return true
  /* Status 0 is "never reached PostgREST at all" and is diagnosed
   * separately from every HTTP status on purpose — "never reached" and
   * "refused" are different problems, and Phase 2 Step 4 is where this
   * project learned that conflating them sends the reader to fix the
   * wrong thing. */
  if (status === 0) return false
  if (status === 401) return false  // handled by one refresh, one retry
  if (status === 429) return false  // rate limited — the one retryable 4xx
  if (status >= 500) return false
  if (status >= 400) return true    // any other 4xx: the request is wrong
  return false
}

function signedOut() {
  return new RemoteError(
    'not signed in — Currency Keep cannot reach your vault without a session',
    { status: 401, permanent: true, hint: 'remote.no_session', userMessage: MSG.signedOut },
  )
}

function fail(res, what, userMessage) {
  const permanent = isPermanent(res.status, res.code, res.hint)
  const detail = [res.code, res.message].filter(Boolean).join(' ')
  throw new RemoteError(
    `${what} failed: HTTP ${res.status}${detail ? ` — ${detail}` : ''}`,
    { status: res.status, code: res.code, hint: res.hint, permanent, userMessage },
  )
}

/* ── one HTTP helper ───────────────────────────────────────────────── */

async function rest(method, path, { body, headers = {}, token } = {}) {
  let res
  let text
  try {
    res = await fetch(`${BASE()}/rest/v1/${path}`, {
      method,
      headers: {
        apikey: ANON_KEY(),
        authorization: `Bearer ${token}`,
        'content-type': 'application/json',
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    text = await res.text()
  } catch (e) {
    throw new RemoteError(
      `could not reach the database (${e?.message ?? 'network error'})`,
      { status: 0, permanent: false, userMessage: MSG.offline },
    )
  }

  let parsed = null
  try { parsed = text ? JSON.parse(text) : null } catch (e) { /* not JSON */ }

  return {
    status: res.status,
    rows: Array.isArray(parsed) ? parsed : null,
    body: parsed,
    code: parsed?.code ?? null,
    hint: parsed?.hint ?? null,
    message: parsed?.message ?? null,
    contentRange: res.headers.get('content-range'),
  }
}

/**
 * Run a request with a live token, and on a 401 refresh ONCE and retry
 * ONCE.
 *
 * ⚠ NEVER A LOOP. `refresh()` clears the session when the refresh token
 * is spent or revoked, so a second 401 means signed out for real — and
 * `auth-check` proves a refused refresh fails closed rather than
 * spinning. Retrying past that point turns one expired token into an
 * unbounded stream of requests that cannot succeed.
 *
 * `getToken()` alone is not enough here: it only refreshes when the
 * token is CLOSE TO EXPIRING, so a token that is rejected while still
 * looking fresh — a revoked session, a clock disagreement — would be
 * handed back unchanged. A 401 is the server's opinion, and it wins.
 */
async function authed(run) {
  const token = await getToken()
  if (!token) throw signedOut()

  const first = await run(token)
  if (first.status !== 401) return first

  const session = await refresh()
  if (!session?.access_token) throw signedOut()

  const second = await run(session.access_token)
  if (second.status === 401) throw signedOut()
  return second
}

function requireUid() {
  const id = getSession()?.user?.id
  if (!id) throw signedOut()
  return id
}

/* ── session state ─────────────────────────────────────────────────── */

/*
 * ⚠ THE MOST IMPORTANT STATE IN THIS FILE.
 *
 * `_read` is set ONLY by a read that returned 200 AND whose row count
 * was verified against `content-range`. Every write is refused while it
 * is null. It is not a cache flag — it is the answer to "is the thing I
 * am about to full-replace something I actually saw?"
 */
let _read = null
let _reading = null
let _half = { collection: [], custom: [] }

let _profile = null
let _profileRead = false
let _profileReading = null

/**
 * `content-range: 0-11/40` → 40. An empty table reports the total after
 * the slash with a star before it → 0. Anything unusable → null.
 */
function parseTotal(cr) {
  if (!cr) return null
  const total = String(cr).split('/')[1]
  if (total === undefined || total === '*') return null
  const n = Number(total)
  return Number.isInteger(n) && n >= 0 ? n : null
}

function parseJson(value, key) {
  try {
    return JSON.parse(String(value))
  } catch (e) {
    throw new RemoteError(`${key} was not valid JSON`, {
      permanent: true, hint: 'remote.bad_json', userMessage: MSG.saveFailed,
    })
  }
}

/* ── reading the vault ─────────────────────────────────────────────── */

async function readVault() {
  const uid = requireUid()

  /* `order=created_at.asc,id.asc` — carry-forward #4, Jesse 2026-08-08.
   * The app's default sort is "added", which was array order inside a
   * blob; a table has none. `id.asc` is the tiebreaker, because two
   * holdings added in the same transaction share a timestamp and an
   * unstable sort would reshuffle the vault between loads.
   *
   * `sync_holdings` deliberately keeps `created_at` out of its upsert
   * SET list, so an edit cannot reorder a collection. */
  const res = await authed((token) => rest(
    'GET',
    `holdings?user_id=eq.${uid}&select=*&order=created_at.asc,id.asc`,
    { token, headers: { prefer: 'count=exact' } },
  ))

  /* ⚠ 206 IS A RESPONSE, NOT A FAILURE, AND THE DIFFERENCE IS THE WHOLE
   * POINT OF THE NEXT TWENTY LINES. PostgREST answers a ranged request
   * with 206 Partial Content — so a stray Range header lands here, and
   * rejecting it as "not 200" would refuse the read for a reason that
   * says nothing about what happened. A truncation can equally arrive as
   * a plain 200 (PostgREST's own max-rows ceiling does exactly that), so
   * the status cannot be the detector in either direction.
   *
   * Let both through and let the row count decide. Found by
   * `storage-check` on its first run, which reported the short read as a
   * generic read failure — right outcome, wrong diagnosis, which is the
   * sibling failure this project keeps meeting: a check that cannot
   * distinguish one failure from another. */
  if ((res.status !== 200 && res.status !== 206) || !res.rows) {
    fail(res, 'reading your vault', MSG.readFailed)
  }

  /* ⚠ RULE 2. A read whose total cannot be established is NOT a good
   * read. Treating "no usable content-range" as "however many rows
   * arrived" is exactly the check that cannot tell the passing case from
   * the failing one — and the failing case here deletes notes. */
  /* ⚠ NAMED `declaredTotal`, NOT `total`, AND THAT IS NOT STYLE.
   * `readAll` below does the same comparison for the community reads,
   * and with both spelled `total` the two `if` lines were BYTE-IDENTICAL
   * — which is exactly the trap Step 3 logged when `String.replace`
   * sabotaged `textIn` instead of `moneyIn` and the gate went green for
   * an unrelated reason. `storage-sabotage` anchors on this line, so a
   * duplicate would leave case 1 silently testing the wrong function.
   * The harness now also asserts each anchor is unique; this makes the
   * mistake unavailable rather than merely detected. */
  const declaredTotal = parseTotal(res.contentRange)
  if (declaredTotal === null) {
    throw new RemoteError(
      `the vault read could not be verified — content-range was ${res.contentRange ?? 'absent'}`,
      { status: res.status, permanent: false, hint: 'remote.unverifiable_read', userMessage: MSG.readFailed },
    )
  }
  if (res.rows.length !== declaredTotal) {
    throw new RemoteError(
      `short read: received ${res.rows.length} of ${declaredTotal} holding(s) — treating as a FAILED read, ` +
      'because saving from a truncated vault deletes every holding that did not come back',
      { status: res.status, permanent: false, hint: 'remote.short_read', userMessage: MSG.readFailed },
    )
  }

  /* Both halves, from one snapshot of one set of rows. */
  const { collection, custom } = rowsToVault(res.rows)
  _half = { collection, custom }
  _read = { at: Date.now(), count: collection.length }
  return _half
}

/* Single-flight, the same idiom as auth.js's refresh and idb.js's cached
 * open promise. The app loads state, the profile and photos together, so
 * concurrency here is the normal case rather than a rare one. */
function ensureVault() {
  if (_read) return Promise.resolve(_half)
  if (_reading) return _reading
  _reading = readVault().finally(() => { _reading = null })
  return _reading
}

/* ── writing the vault ─────────────────────────────────────────────── */

async function syncVault() {
  /* ⚠ THE SESSION IS CHECKED FIRST, AND THE ORDER IS NOT COSMETIC.
   * Both refusals are permanent and both keep the vault safe, so it
   * would be easy to think the order does not matter. It decides what
   * the collector is told: with the read guard first, a signed-out save
   * reports "could not load your vault" — confident, specific, and about
   * the wrong problem, when the answer is "sign in". `storage-check`
   * caught this on its first run. */
  requireUid()

  /* ⚠ RULE 1. The guard the database cannot be. */
  if (!_read) {
    throw new RemoteError(
      'refusing to save: this vault was never successfully read in this session, so a full-replace ' +
      'sync would delete every holding the read did not return. See BUILD_NOTES, Phase 3 Step 4.',
      { permanent: true, hint: 'remote.no_successful_read', userMessage: MSG.readFailed },
    )
  }

  let rows
  try {
    rows = vaultToRows(_half.collection, indexCustom(_half.custom))
  } catch (e) {
    /* The mapper refuses rather than guesses — a non-uuid id, an
     * unrepresentable value, a quick-add holding whose entry did not
     * come along. All permanent: retrying changes nothing. */
    throw new RemoteError(`this vault cannot be written as rows: ${e?.message ?? e}`, {
      permanent: true, hint: 'remote.unmappable', userMessage: MSG.saveFailed,
    })
  }

  const args = { p_holdings: rows }
  /* ⚠ RULE 3. Reachable only after a verified read, which is what makes
   * it "the collector deleted their last note" rather than "the read
   * failed and the vault looks empty". Deleting your last note has to
   * keep working, or the guard is a bug rather than a guard. */
  if (rows.length === 0) args.p_allow_empty = true

  const res = await authed((token) => rest('POST', 'rpc/sync_holdings', { token, body: args }))
  if (res.status !== 200) fail(res, 'saving your vault', MSG.saveFailed)

  /* The RPC returns the resulting row count so the caller can confirm
   * what it sent is what now exists, "rather than assuming a 200 means
   * the vault is right" — db/005 §5. Assert it. */
  const remaining = typeof res.body === 'number' ? res.body : null
  if (remaining !== null && remaining !== rows.length) {
    throw new RemoteError(
      `sync reported ${remaining} holding(s) after sending ${rows.length} — the vault is not what was sent`,
      { status: res.status, permanent: false, hint: 'remote.count_mismatch', userMessage: MSG.saveFailed },
    )
  }

  _read = { at: Date.now(), count: rows.length }
  return rows.length
}

/* ── the profile ───────────────────────────────────────────────────── */

async function readProfile() {
  const uid = requireUid()
  const res = await authed((token) => rest('GET', `profiles?id=eq.${uid}&select=*`, { token }))
  if (res.status !== 200 || !res.rows) fail(res, 'reading your profile', MSG.readFailed)

  /* The signup trigger creates this row, and `rls-check.mjs:388` already
   * depends on that. Exactly one row or something is wrong — and a
   * missing profile must not read as an empty one, because an empty one
   * is what gets written back over the real thing. */
  if (res.rows.length !== 1) {
    throw new RemoteError(
      `expected exactly 1 profile row, got ${res.rows.length}`,
      { status: res.status, permanent: false, hint: 'remote.no_profile_row', userMessage: MSG.readFailed },
    )
  }

  _profile = rowToProfile(res.rows[0])
  _profileRead = true
  return _profile
}

/* ── the repair — Phase 5 step 5, his ruling 2026-09-11 (shape A) ──── */

/**
 * Put back a missing `profiles` row, for the signed-in user only.
 *
 * `db/011`'s `repair_profile()` is SECURITY DEFINER, takes NO argument,
 * reads the id from `auth.uid()`, inserts the same three columns the
 * signup trigger inserts and lets the table choose every other default,
 * and is a no-op when the row exists. This module chooses nothing: no
 * column, no id, an empty body.
 *
 * ⚠ THE 200 IS NOT THE EVIDENCE — THE ROW IS. After the call the profile
 * is re-read through `readProfile()` with the cache dropped, so what this
 * resolves with is what the server holds, and a repair that did not land
 * still surfaces as `remote.no_profile_row` — the same hint the screen
 * was opened on. `signup-check` learned this shape the hard way on
 * 2026-09-10: a module's word for a server's answer is a proxy.
 */
async function repairProfile() {
  requireUid()
  const res = await authed((token) => rest('POST', 'rpc/repair_profile', { token, body: {} }))
  if (res.status < 200 || res.status >= 300) fail(res, 'repairing your profile', MSG.readFailed)
  _profile = null
  _profileRead = false
  return readProfile()
}

function ensureProfile() {
  if (_profileRead) return Promise.resolve(_profile)
  if (_profileReading) return _profileReading
  _profileReading = readProfile().finally(() => { _profileReading = null })
  return _profileReading
}

async function writeProfile(p) {
  /* Session first, for the same reason as syncVault. */
  requireUid()

  if (!_profileRead) {
    throw new RemoteError(
      'refusing to save your profile: it was never successfully read in this session. ' +
      'community.js:16 swallows a failed read and leaves username "" and isPublic false, and ' +
      "db/001's username_lock allows one change per YEAR — so this save could cost a name for twelve months.",
      { permanent: true, hint: 'remote.no_profile_read', userMessage: MSG.readFailed },
    )
  }

  /* `profileToRow` deliberately does not send `username_set_at`: the
   * trigger maintains it server-side and overwrites whatever a client
   * sends, so sending it would suggest the client has a say in when its
   * own lock expires. */
  const res = await authed((token) => rest('PATCH', `profiles?id=eq.${requireUid()}`, {
    token,
    body: profileToRow(p),
    headers: { prefer: 'return=representation' },
  }))

  if (res.status >= 300 || !res.rows) fail(res, 'saving your profile', MSG.saveFailed)

  /* ⚠ A DENIED WRITE LOOKS EXACTLY LIKE A SUCCESSFUL ONE. Postgres does
   * not refuse an UPDATE that RLS filters out — it matches zero rows and
   * returns 200. `return=representation` is what makes the difference
   * visible: one row back means one row was actually written. Phase 3
   * Step 0 learned this on the gate; it is just as true in the app. */
  if (res.rows.length !== 1) {
    throw new RemoteError(
      `the profile update matched ${res.rows.length} rows — nothing was written`,
      { status: res.status, permanent: true, hint: 'remote.write_matched_nothing', userMessage: MSG.saveFailed },
    )
  }

  _profile = rowToProfile(res.rows[0])
  return _profile
}

/* ── the community, synthesised ────────────────────────────────────── */

/*
 * ⚠ THE COMMUNITY TAB NEEDS NO APPLICATION CHANGES AT ALL, AND THAT IS
 * THE POINT OF HAVING A SEAM.
 *
 * `community.js` reads public profiles as blobs in a KV store:
 *
 *   list("notevault:pub:")            -> ["notevault:pub:alice", ...]
 *   get("notevault:pub:alice")        -> {username, notes:[{c,g,y,hid,hasF,hasB,q}]}
 *
 * Those blobs used to be written by `publish()` into IndexedDB — a
 * SNAPSHOT, re-written on every add, every grade change and every photo
 * save by `republish()`, and stale the moment any of that failed.
 *
 * They are now DERIVED, here, from `public_profiles` and
 * `public_holdings`. `loadPublic()` and `vCommunity()` are untouched —
 * not "barely changed", untouched — and a whole class of stale-snapshot
 * bugs stops existing because there is no snapshot. (⚠ `getPubPhoto()`
 * was named here too; Step 7 bullet 3 deleted it, 2026-08-28.)
 *
 * ⚠ AND `publish()` / `unpublish()` / `republish()` STOP BEING WRITES.
 * A `set` on one of these keys is accepted and does nothing, because the
 * thing it wanted to store is now computed. That is not a swallowed
 * failure: `is_public` on the profile row is the whole of publishing,
 * and `saveProfile()` already writes it.
 *
 * ── WHAT A STRANGER'S PROFILE CANNOT SHOW IN PHASE 3 ───────────────
 * TWO THINGS, both structural rather than missing work:
 *
 * 1. PHOTOS. `photo_front_ref` is `idb:<holdingId>:front` — a pointer
 *    into the OWNER'S IndexedDB. Another collector's browser has
 *    nothing at that key and never will. Your own public profile still
 *    shows photos, because those bytes are on your device. This is
 *    exactly what Phase 4 and R2 are for, and the ref column was
 *    designed so that becomes a value rewrite.
 * 2. ~~QUICK-ADD NOTES.~~ ✅ CLOSED 2026-08-29 by db/009 and by
 *    `rowToPublicNote` below. It read: *"`custom_entry` is private, so
 *    `cat(x.c)` is undefined for a stranger and `views-community.js:34`
 *    filters the note out."* True for twenty-one days after Jesse
 *    decided otherwise on 2026-08-08 — *"custom notes SHOULD be public
 *    when a profile is public"* — and it meant a stranger saw ONE
 *    QUARTER of his four holdings.
 *
 *    ⚠ AND THE SCOPE NOTE THAT USED TO END THIS PARAGRAPH WAS THE
 *    ACCURATE PART: *"a real decision about which fields inside the
 *    blob a stranger sees, not a line of wiring."* It was. The view
 *    projects FOUR fields — fr, denom, fd, section — and the argument
 *    for not passing the blob whole is in db/009's header.
 */

/* ⚠⚠ THE TRAILING COLON WAS LOAD-BEARING AND ITS COUNTERPART IS GONE.
 * It separated this prefix from `notevault:pubph:<user>:<hid>:<slot>` —
 * one character, the colon at index 13 — and `storage-check` and `smoke`
 * pinned the two side by side. **Step 7 bullet 3 deleted the pubph key
 * family (2026-08-28), so nothing else in the store now starts with
 * `notevault:pub`, and both of those assertions were deleted with it
 * rather than left green against an absent subject.**
 *
 * ⚠ KEEP THE COLON ANYWAY. It is still the difference between an exact
 * namespace and a prefix that would swallow any future `notevault:pubX`
 * key, and the next such key would reintroduce the trap silently. */
const PUB_PREFIX = 'notevault:pub:'

let _community = null
let _communityReading = null

/**
 * `{c,g,y,hid,hasF,hasB,q,n}` — the shape `community.js`'s `snapshot`
 * builds, plus `n`.
 *
 * ⚠⚠ `q` SINCE 2026-09-21 (db/012): the EPQ/PPQ designation, public on
 * his ruling. Here it comes straight off `public_holdings.qualifier`,
 * with **no compatibility read** — and the asymmetry with `snapshot` is
 * correct, not an oversight: `snapshot` projects the OWNER'S OWN
 * in-memory holdings, where a memo mark may not have been carried over
 * yet, while this reads a ROW THAT HAS BEEN SAVED, and any save since
 * the carry-over shipped has written the column. ⚠ A row last saved by
 * an OLD client would read `false` here with the letters still in its
 * private `personal_note` — and that memo is not projected, so there is
 * nothing to read it from even if we wanted to. That is the transition's
 * one accepted gap and it closes on the owner's next save.
 *
 * ⚠⚠ `n` IS THE QUICK-ADD ENTRY, RESOLVED HERE AND CARRIED ON THE NOTE.
 * Null for a catalog holding, which is every holding whose
 * `catalog_sid` is not `CUSTOM:…` — db/004's CHECK makes those two the
 * same statement, so presence is a reliable branch and no second signal
 * is needed.
 *
 * ⚠⚠⚠ AND IT IS DELIBERATELY NOT REGISTERED INTO `BY_SID`. The obvious
 * shortcut is `regCustom(entry)` so that `cat(x.c)` resolves a
 * stranger's note the way the owner's own vault does. DO NOT. `BY_SID`
 * is ONE GLOBAL, shared with the vault renderer, and a quick-add sid is
 * `'CUSTOM:' + Date.now() + rand(1000)` (custom.js:27). A collision
 * across two devices is unlikely and its blast radius is not the
 * stranger's screen — it is THE OWNER'S OWN NOTE rendering as somebody
 * else's. Carrying the entry on the note costs nothing and cannot do
 * that. Its three readers all take the shape `x.n || cat(x.c)`.
 *
 * ⚠ `sid` and `cust` are rebuilt by `rowToCustomEntry` from
 * `catalog_sid`; `cust: true` is load-bearing rather than decorative,
 * because `cardTitle` and `cardSub` (community.js:113-114) branch on
 * `isCust(n)`. Without it a quick-add note renders as
 * "Custom entry · Fr. " with an empty number.
 *
 * ⚠ THE PROJECTED FIELDS ARE THE VIEW'S BUSINESS, NOT THIS FUNCTION'S.
 * (~~FOUR~~ — the count moved with db/012 and a number in a caption is
 * method rule 7's shape, so it is gone rather than corrected.) This
 * is a pass-through: whatever `public_holdings` projects is what a
 * stranger gets. Filtering here would be theatre — the bytes have
 * already crossed the wire by the time this runs. The boundary is
 * db/009 and it is the only boundary.
 */
function rowToPublicNote(row) {
  return {
    c: textIn(row.catalog_sid),
    g: textIn(row.grade),
    y: textIn(row.series_year),
    hid: row.id,
    hasF: row.photo_front_ref != null,
    hasB: row.photo_back_ref != null,
    q: row.qualifier === true,
    n: rowToCustomEntry(row),
  }
}

async function readAll(path, what) {
  const res = await authed((token) => rest('GET', path, { token, headers: { prefer: 'count=exact' } }))
  if ((res.status !== 200 && res.status !== 206) || !res.rows) fail(res, what, MSG.readFailed)
  const total = parseTotal(res.contentRange)
  /* Same discipline as the vault read. Nothing here can DELETE anything
   * — the community path is read-only — so a short read is not a
   * data-loss risk. It is still a wrong answer presented as a right
   * one: a collector's profile silently missing half its notes, or
   * missing from the list entirely, with nothing saying so. */
  if (total === null) {
    throw new RemoteError(`${what} could not be verified — content-range was ${res.contentRange ?? 'absent'}`,
      { status: res.status, permanent: false, hint: 'remote.unverifiable_read', userMessage: MSG.readFailed })
  }
  if (res.rows.length !== total) {
    throw new RemoteError(`${what}: received ${res.rows.length} of ${total}`,
      { status: res.status, permanent: false, hint: 'remote.short_read', userMessage: MSG.readFailed })
  }
  return res.rows
}

/**
 * Every public profile and its notes, in one pass.
 *
 * ⚠ ORDERED BY `catalog_sid`, NOT BY TIME. Jesse's call, 2026-08-08,
 * closing an item open since 2026-08-07. The app's "added" sort was
 * array order inside the snapshot blob, and a table has none — but
 * `created_at` is deliberately NOT in `public_holdings`, because
 * per-note timestamps reveal when a collector was active and a
 * timestamp that has been public cannot be made un-public. Catalog
 * order needs no new column, no new exposure, and is arguably a better
 * way to browse somebody else's collection than the order they happened
 * to type it in. `id` is the tiebreaker so the order is stable between
 * loads.
 */
async function readCommunity() {
  const profiles = await readAll(
    'public_profiles?select=id,username&order=username.asc', 'reading the community')
  const holdings = await readAll(
    'public_holdings?select=*&order=catalog_sid.asc,id.asc', 'reading public collections')

  const byUser = new Map()
  /* ⚠ `id` RIDES ALONG AS OF 2026-09-07 — the brand client half. A logo
   * or banner is read through the batch route by the OWNER'S ID (the
   * profile row's `id`, which is what `logo_ref` hangs off), and the
   * username was the only thing this snapshot carried. Opaque to every
   * other reader; the demo profiles have none and are never asked. */
  for (const p of profiles) byUser.set(p.id, { id: p.id, username: textIn(p.username), notes: [] })
  for (const h of holdings) byUser.get(h.user_id)?.notes.push(rowToPublicNote(h))

  const byName = new Map()
  for (const prof of byUser.values()) {
    if (prof.username) byName.set(prof.username, { ...prof, updated: Date.now() })
  }
  _community = byName
  return byName
}

function ensureCommunity() {
  if (_community) return Promise.resolve(_community)
  if (_communityReading) return _communityReading
  _communityReading = readCommunity().finally(() => { _communityReading = null })
  return _communityReading
}

/* ── the seam ──────────────────────────────────────────────────────── */

const backend = {
  async get(key, shared) {
    const k = String(key)
    if (k === COLLECTION_KEY) {
      const half = await ensureVault()
      return { key: k, value: JSON.stringify(half.collection) }
    }
    if (k === CUSTOM_KEY) {
      /* The same snapshot the collection came from. A second read here
       * would be a second, differently-timed view of the same rows. */
      const half = await ensureVault()
      return { key: k, value: JSON.stringify(half.custom) }
    }
    if (k === PROFILE_KEY) {
      return { key: k, value: JSON.stringify(await ensureProfile()) }
    }
    if (k.startsWith(PUB_PREFIX)) {
      /* Served from the SAME snapshot the list came from. A second read
       * per profile would be N round trips and, worse, N differently
       * timed views of one community. */
      const snap = (await ensureCommunity()).get(k.slice(PUB_PREFIX.length))
      /* Absent must be null, not a record with no value — community.js:66
       * does `g && g.value`, and the contract idb.js documents is that a
       * key that is not there comes back null. */
      return snap ? { key: k, value: JSON.stringify(snap) } : null
    }
    return local.get(k, shared)
  },

  async set(key, value, shared) {
    const k = String(key)
    if (k === COLLECTION_KEY) {
      _half = { collection: parseJson(value, k), custom: _half.custom }
      await syncVault()
      return { key: k }
    }
    if (k === CUSTOM_KEY) {
      _half = { collection: _half.collection, custom: parseJson(value, k) }
      await syncVault()
      return { key: k }
    }
    if (k === PROFILE_KEY) {
      await writeProfile(parseJson(value, k))
      return { key: k }
    }
    if (k.startsWith(PUB_PREFIX)) {
      /* ⚠ ACCEPTED, AND DELIBERATELY DOES NOTHING. This is `publish()`
       * (community.js:19) asking to store a snapshot of the caller's own
       * vault. That snapshot is now DERIVED from public_holdings, so
       * there is nothing to store — and publishing itself is
       * `is_public` on the profile row, which `saveProfile()` already
       * writes.
       *
       * A no-op is the honest answer rather than a swallowed failure:
       * the caller's intent (be visible in the Community) is satisfied,
       * by a different mechanism, before this is ever called.
       *
       * The cache is dropped so the collector's own next look at the
       * Community reflects what they just changed. */
      _community = null
      return { key: k }
    }
    return local.set(k, value, shared)
  },

  async delete(key, shared) {
    const k = String(key)
    if (k.startsWith(PUB_PREFIX)) {
      /* `unpublish()` (community.js:20). Same reasoning as the set
       * above: going private is `is_public = false` on the profile row,
       * which saveProfile() has already written by the time this runs.
       * The derived snapshot then disappears on its own, because the
       * view's WHERE clause stops matching. */
      _community = null
      return { key: k }
    }
    if (REMOTE_KEYS.includes(k)) {
      /* No call site does this today. If one ever does, it means
       * "delete my whole vault", and that is a deliberate account
       * action rather than something the blob seam should express by
       * accident — `storage.js:33` swallows every delete error, so a
       * wrong answer here would be completely silent. */
      throw new RemoteError(
        `${k} cannot be deleted through the seam — it is rows in Postgres, not a blob`,
        { permanent: true, hint: 'remote.delete_unsupported', userMessage: MSG.saveFailed },
      )
    }
    return local.delete(k, shared)
  },

  async list(prefix, shared) {
    const p = prefix == null ? '' : String(prefix)
    if (p.startsWith(PUB_PREFIX)) {
      /* `loadPublic()` (community.js:65) enumerating the community. The
       * keys are synthesised from `public_profiles`; every one of them
       * is then fed straight back into get() above, out of the same
       * snapshot. */
      const snap = await ensureCommunity()
      return { keys: [...snap.keys()].map((u) => PUB_PREFIX + u) }
    }
    /* Fail closed rather than answer about half the store. Today the
     * only callers are `notevault:pub:` (community.js:65) and
     * `notevault:pubph:<name>:` (community.js:55), neither of which
     * overlaps a remote key — but a future prefix that did would get a
     * confidently short list with no indication anything was missing. */
    const shadowed = REMOTE_KEYS.filter((k) => k.startsWith(p))
    if (shadowed.length) {
      throw new RemoteError(
        `list("${p}") would have to enumerate ${shadowed.join(', ')}, which live in Postgres rather than IndexedDB`,
        { permanent: true, hint: 'remote.list_shadowed', userMessage: MSG.readFailed },
      )
    }
    return local.list(p, shared)
  },
}

/**
 * Attach the backend as window.storage.
 *
 * MUST run before storage.js evaluates `const HAS = ... && window.storage`,
 * which is why storage.js calls this at the top of its own body. See
 * scripts/extract.mjs — the call is generated, and a hand edit in
 * src/storage.js is silently reverted by the next `npm run extract`.
 *
 * ⚠ UNLIKE idb.js, THIS INSTALLS EVEN WHEN INDEXEDDB IS UNAVAILABLE, and
 * the change is deliberate. idb.js installs nothing in that case, so the
 * app browses without saving. Here the notes live in Postgres and only
 * the photos need IndexedDB — so installing means notes still save and
 * photo calls fail on their own. Strictly better than losing both.
 *
 * Persistence is still requested, because the photos are still local.
 */
function installRemoteStorage() {
  if (typeof window === 'undefined') return null
  // Never clobber a host-provided backend (the artifact sandbox had one).
  if (window.storage) return window.storage
  window.storage = backend
  requestPersistence()
  return backend
}

/** Test seam: drop cached state so a gate can start from nothing. */
function _reset() {
  _read = null
  _reading = null
  _half = { collection: [], custom: [] }
  _profile = null
  _profileRead = false
  _profileReading = null
  _community = null
  _communityReading = null
}

/** Test seam: what the module believes, without touching the network. */
function _peek() {
  return {
    read: _read ? { ..._read } : null,
    collection: _half.collection,
    custom: _half.custom,
    profileRead: _profileRead,
    community: _community ? [..._community.keys()] : null,
  }
}

export {
  COLLECTION_KEY,
  CUSTOM_KEY,
  MSG,
  PROFILE_KEY,
  PUB_PREFIX,
  REMOTE_KEYS,
  RemoteError,
  backend,
  installRemoteStorage,
  isPermanent,
  parseTotal,
  repairProfile,
  _peek,
  _reset,
}
